import { orderHistoryService } from '@/services/order-history.service';
import { useQuery, useMutation } from '@tanstack/react-query';
import { SearchParams } from '@/types/orderhistory';
import { productDetailService } from '@/services/product-detail.service';

export const useQueryOrderHistoryList = (searchParams: SearchParams) => {
  const { pageNumber, pageSize, orderBy, advanceSearchParams } = searchParams;

  return useQuery({
    queryKey: ['ORDER_HISTORY_LIST', pageNumber, pageSize, orderBy, advanceSearchParams],
    queryFn: async () => {
      const data = await orderHistoryService.getOrderList(searchParams);
      return data;
    },
  });
};
export const useQueryMiniOrder = (orderNumber: string, webOrderNumber: string) => {
  return useQuery({
    queryKey: ['ORDER_MINI_DETAILS_', orderNumber, webOrderNumber],
    queryFn: async () => {
      const data = await orderHistoryService.getMiniOrder(orderNumber, webOrderNumber);
      return data;
    },
  });
};

export const useQueryOrderDetails = (orderNumber: string, webOrderNumber: string) => {
  return useQuery({
    queryKey: ['ORDER_DETAILS_', orderNumber, webOrderNumber],
    queryFn: async () => {
      const data = await orderHistoryService.getOrderDetails(orderNumber, webOrderNumber);
      return data;
    },
    refetchOnMount: false,
  });
};

export const useMutationReorder = () => {
  return useMutation({
    mutationFn: (items: Array<{ catalogNumber: string; quantity: number }>) => orderHistoryService.reOrder(items),
  });
};

export const useMutationExportOrder = () => {
  return useMutation({
    mutationFn: (searchParams: SearchParams) => orderHistoryService.exportOrder(searchParams),
  });
};

export const useQueryPriceContract = (orderNumber: string) => {
  return useQuery({
    queryKey: ['ORDER_PRICE_CONTRACT_' + orderNumber],
    queryFn: async () => {
      const data = await orderHistoryService.getPriceContract(orderNumber);
      return data.data;
    },
  });
};

export const useQueryCoaDocument = (lotNumber: string, catalogNumber: string) => {
  return useQuery({
    queryKey: ['ORDER-HISTORY', 'COA', lotNumber, catalogNumber],
    queryFn: async () => {
      const res = await productDetailService.getCoaInfo({ lotNumber, sku: catalogNumber, pageNo: 1, pageSize: 5 });
      return res;
    },
    keepPreviousData: true,
    enabled: catalogNumber && lotNumber ? true : false,
  });
};

export const useMutationDispatchNote = () => {
  return useMutation({
    mutationFn: (orderNumber: string) => orderHistoryService.getDispatchNote(orderNumber),
  });
};

export const useMutationInvoice = () => {
  return useMutation({
    mutationFn: (orderNumber: string) => orderHistoryService.getInvoice(orderNumber),
  });
};

export const useQueryCancelStatus = (params) => {
  const { orderNumber, isShowCancelOrder } = params;
  return useQuery({
    queryKey: ['ORDER_CANCEL_STATUS', orderNumber],
    queryFn: async () => {
      const data = await orderHistoryService.getCancelStatus(params);
      return data;
    },
    enabled: isShowCancelOrder ? true : false,
  });
};
export const useMutationCancelStatus = () => {
  return useMutation({
    mutationFn: (data: any) => orderHistoryService.getCancelStatus(data),
  });
};
export const useMutationCancelOrder = () => {
  return useMutation({
    mutationFn: (data: any) => orderHistoryService.cancelOrder(data),
  });
};
export const useMutationFileUpload = () => {
  return useMutation(({ file, orderNumber }: { file: any; orderNumber: string }) => {
    return orderHistoryService.uploadAttachment(file, orderNumber);
  });
};
export const useQueryGetDeliveryData = (dnNumbers) => {
  return useQuery({
    queryKey: ['DN_DATA', dnNumbers],
    queryFn: async () => {
      const data = await orderHistoryService.getDeliveryDetails(dnNumbers);
      return data;
    },
  });
};
export const useQueryCheckDeliveryConfirmation = (dnNumbers) => {
  return useQuery({
    queryKey: ['CHECK_DELIVERY_CONFIRMATION', dnNumbers],
    queryFn: async () => {
      const data = await orderHistoryService.checkDeliveryConfirmation(dnNumbers);
      return data;
    },
  });
};
export const useMutationDeliveryConfirmation = () => {
  return useMutation(({ dnNumbers, option }: { dnNumbers: string; option: any }) => {
    return orderHistoryService.updateDeliveryConfirmation({ dnNumbers, option });
  });
};
export const useQueryGetCancelReason = (isCN) => {
  return useQuery({
    queryKey: ['CANCEL_REASON_DATA'],
    queryFn: async () => {
      const data = await orderHistoryService.cancelReasonData();
      return data;
    },
    enabled: !isCN,
  });
};
